/**
 * "Recently used tools" for the dashboard.
 *
 * Phase 0 is a single shared password, so the server can't tell one person from
 * another — a server-side history would be everyone's history. This keeps it in
 * localStorage instead: genuinely per-person, no backend, and swappable for a
 * real store when per-account auth lands (Phase 2).
 *
 * Title and department are denormalised into each entry so the dashboard can
 * paint the recents row instantly, without waiting on the catalog fetch.
 */
export type RecentTool = {
  id: string
  title: string
  department: string
  to: string
  isDraft: boolean
  lastUsedAt: string
}

const KEY = 'aeys.vco.recents'
/**
 * One row of cards. "Pick up where you left off" is a shortcut back to the last
 * few things you touched; past three it stops being a shortcut and becomes a
 * second catalog above the real one.
 */
const MAX = 3

/** localStorage throws in Safari private mode and is absent in some test envs. */
function store(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

// `to` and `isDraft` are optional here: neither existed before these fields
// were added, so a stored entry written by an older build of the app won't
// have them.
type StoredRecent = Omit<RecentTool, 'to' | 'isDraft'> & { to?: string; isDraft?: boolean }

function isRecent(value: unknown): value is StoredRecent {
  if (typeof value !== 'object' || value === null) return false
  const r = value as Record<string, unknown>
  return (
    typeof r.id === 'string' &&
    typeof r.title === 'string' &&
    typeof r.department === 'string' &&
    typeof r.lastUsedAt === 'string' &&
    (r.to === undefined || typeof r.to === 'string') &&
    (r.isDraft === undefined || typeof r.isDraft === 'boolean')
  )
}

/**
 * Reads the stored history, newest first. Anything malformed — absent key, bad
 * JSON, a non-array, entries missing fields — degrades to an empty list rather
 * than throwing: a corrupt cache must never take the dashboard down.
 *
 * An entry written before `to` existed is migrated to the bespoke-tool route —
 * the only route such an entry could ever have opened, since that field is
 * what fixed the dead link — rather than left to throw downstream. An entry
 * written before `isDraft` existed has no recorded status, so it fails safe to
 * draft: the same "anything other than an explicit approved is draft" rule
 * applied everywhere else in the project, not a special case here.
 */
export function readRecents(): RecentTool[] {
  const raw = store()?.getItem(KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(isRecent)
      .slice(0, MAX)
      .map((r) => ({ ...r, to: r.to ?? `/tools/${r.id}`, isDraft: r.isDraft ?? true }))
  } catch {
    return []
  }
}

/**
 * Records a tool (or topic document) as just-used and returns the updated
 * list. Re-opening it moves it to the front rather than duplicating it; the
 * list is capped at MAX. `to` defaults to the bespoke-tool route so a caller
 * that doesn't have a topic route doesn't need to compute one; `isDraft` has
 * no such default — every call site must say explicitly whether what it just
 * recorded is approved, the same safety property every other listing enforces.
 */
export function recordToolUse(
  tool: { id: string; title: string; department: string; isDraft: boolean; to?: string },
  now: Date = new Date(),
): RecentTool[] {
  const entry: RecentTool = {
    id: tool.id,
    title: tool.title,
    department: tool.department,
    to: tool.to ?? `/tools/${tool.id}`,
    isDraft: tool.isDraft,
    lastUsedAt: now.toISOString(),
  }
  const next = [entry, ...readRecents().filter((r) => r.id !== tool.id)].slice(0, MAX)
  try {
    store()?.setItem(KEY, JSON.stringify(next))
  } catch {
    // Quota exceeded or storage disabled — history is a convenience, not state
    // worth failing a page load over.
  }
  return next
}

const DAY_MONTH = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' })

/** Human "when" label for a recents card: "Just now" … "Yesterday" … "12 Jul". */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return ''

  const mins = Math.floor((now.getTime() - then.getTime()) / 60_000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`

  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  if (hours < 48) return 'Yesterday'

  return DAY_MONTH.format(then)
}
